import { Box, Stack, Typography, useTheme } from '@mui/material';
/* eslint-disable react-hooks/exhaustive-deps */
import { FC, ReactNode, useEffect, useState } from 'react';

import { DateTime } from '../../../utility/luxon';
import MuviTabs from '../../shared/muvi-tabs';
import { SessionsDate } from '@/logic/services/movies';
import { checkIsPrevDay } from '@/logic/helpers/check-is-prev-day';
import { checkIsSameDays } from '@/logic/helpers/check-is-same-days';
import { pxToRem } from '@/theme/typography';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

interface DaysFilterProps {
  sessionsDate: SessionsDate[];
  showTimeDate: string;
  returnFirstEnabledDate: (date: SessionsDate) => void;
  selectDate: (date: string) => void;
}
export const DaysFilter: FC<DaysFilterProps> = ({
  sessionsDate,
  showTimeDate,
  selectDate,
  returnFirstEnabledDate,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const { locale } = useRouter();
  const [activeTab, setActiveTab] = useState<number | boolean>(false);

  useEffect(() => {
    if (sessionsDate?.length > 0 && !showTimeDate) {
      const firstEnabledDate = sessionsDate[0];
      setActiveTab(0);
      returnFirstEnabledDate(firstEnabledDate);
    }
  }, [sessionsDate]);

  useEffect(() => {
    if (showTimeDate) {
      const index = sessionsDate.findIndex(
        (_session: SessionsDate) => _session.date === showTimeDate
      );
      setActiveTab(index);
    }
  }, [showTimeDate, sessionsDate]);

  const handleSessionFilterLabel = (_session: SessionsDate, index: number): ReactNode => {
    let todayShort: string;
    const userDate = new Date();

    if (checkIsPrevDay(userDate, new Date(_session?.date))) {
      todayShort = intl.formatMessage({ id: 'global.now' });
    } else if (checkIsSameDays(userDate, new Date(_session?.date)) && index === 0) {
      todayShort = intl.formatMessage({ id: 'today' });
    } else {
      todayShort = DateTime.fromISO(_session?.date, {
        locale: locale,
      }).weekdayShort;
    }

    const todayNumber = DateTime.fromISO(_session.date, {
      locale: locale,
    }).day;
    const monthShort = DateTime.fromISO(_session.date, {
      locale: locale,
    }).monthShort;
    const fontColor =
      index === activeTab ? theme.palette.common.white : theme.palette.grey[80];

    return (
      <Stack>
        <Typography
          component='span'
          sx={{
            fontWeight: 400,
            fontSize: pxToRem(12),
            LineHeight: pxToRem(24),
            color: fontColor,
            textTransform: 'none',
          }}
        >
          {todayShort}
        </Typography>{' '}
        <Typography
          component='span'
          sx={{
            fontWeight: 900,
            fontSize: pxToRem(16),
            LineHeight: pxToRem(24),
            color: fontColor,
          }}
        >
          {todayNumber}
        </Typography>
        <Typography
          component='span'
          sx={{
            fontWeight: 400,
            fontSize: pxToRem(16),
            LineHeight: pxToRem(24),
            color: fontColor,
          }}
        >
          {monthShort}
        </Typography>
      </Stack>
    );
  };

  return (
    <MuviTabs
      slideSx={{
        width: {
          mobile: '54px',
          tablet: pxToRem(69),
        },
      }}
      swiperSx={{
        paddingX: pxToRem(30),
      }}
      slideToClickedSlide
      slidesPerGroupAuto
    >
      {sessionsDate?.map((session: SessionsDate, index: number) => (
        <Box
          id={`movie-day-${index}`}
          component='button'
          key={session.date}
          sx={{
            width: '100%',
            minWidth: 'unset',
            height: `${pxToRem(92)} !important`,
            background: theme.palette.lightGrey.main,
            borderRadius: '10px',
            border: 'none',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease-in-out',

            ...(activeTab === index && {
              color: `${theme.palette.common.white} !important`,
              backgroundColor: theme.palette.primary.main,
            }),
          }}
          onClick={() => selectDate(session.date)}
        >
          {handleSessionFilterLabel(session, index)}
        </Box>
      ))}
    </MuviTabs>
  );
};

export default DaysFilter;
